import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { config } from '../../utils/config';

function isClerkNetworkError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorMessage = typeof error === 'string' ? error : error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack || '' : '';

  return errorMessage.toLowerCase().includes('clerk') || errorStack.toLowerCase().includes('clerk');
}

export function ClerkErrorHandler() {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    function processClerkError(error: unknown, errorType: string) {
      if (!isClerkNetworkError(error)) {
        return false;
      }

      const errorToLog = error instanceof Error ? error : new Error(String(error));

      if (!config.isDev) {
        Sentry.captureException(errorToLog, {
          tags: { source: 'Clerk' },
        });
      } else {
        console.error(`Clerk error (${errorType}):`, error);
      }

      enqueueSnackbar('Authentication service error. Please try again later.', {
        variant: 'error',
        autoHideDuration: 3000,
      });

      return true;
    }

    function handleError(event: ErrorEvent) {
      const error = event.error || event.message;

      if (config.isDev) {
        console.log('[ClerkErrorHandler] handleError called:', error);
      }

      if (processClerkError(error, 'error event')) {
        event.preventDefault();
      }
    }

    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      const error = event.reason;

      if (config.isDev) {
        console.log('[ClerkErrorHandler] handleUnhandledRejection called:', error);
      }

      if (processClerkError(error, 'unhandled rejection')) {
        event.preventDefault();
      }
    }

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    if (config.isDev) {
      console.log('[ClerkErrorHandler] Error handlers registered');
    }

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [enqueueSnackbar]);

  return null;
}
